import { ItemOrcamento, OrcamentoStatus } from '@/contracts/schemas';

export interface TotaisCalculados {
  subtotal: number;
  valorDesconto: number;
  valorTotal: number;
  valorSinal: number;
  valorRestante: number;
}

/**
 * Calcula o subtotal de um item individual.
 */
export function calculateItemSubtotal(item: Pick<ItemOrcamento, 'quantidade' | 'valorUnitario'>): number {
  const qtd = Math.max(0, item.quantidade || 0);
  const vUnit = Math.max(0, item.valorUnitario || 0);
  return Math.round(qtd * vUnit * 100) / 100;
}

/**
 * Calcula todos os totais e sinal de um orçamento a partir dos itens e regras financeiras.
 */
export function calculateOrcamentoTotals(
  itens: ItemOrcamento[],
  desconto: number = 0,
  tipoDesconto: 'fixo' | 'percentual' = 'fixo',
  percentualSinal: number = 30
): TotaisCalculados {
  const subtotal = itens.reduce((acc, item) => acc + (item.subtotal || calculateItemSubtotal(item)), 0);
  const subtotalArredondado = Math.round(subtotal * 100) / 100;

  let valorDesconto = 0;
  if (tipoDesconto === 'percentual') {
    const perc = Math.min(100, Math.max(0, desconto || 0));
    valorDesconto = (subtotalArredondado * perc) / 100;
  } else {
    valorDesconto = Math.max(0, desconto || 0);
  }
  valorDesconto = Math.min(subtotalArredondado, Math.round(valorDesconto * 100) / 100);

  const valorTotal = Math.max(0, Math.round((subtotalArredondado - valorDesconto) * 100) / 100);

  const percSinal = Math.min(100, Math.max(0, percentualSinal ?? 30));
  const valorSinal = Math.round((valorTotal * (percSinal / 100)) * 100) / 100;
  const valorRestante = Math.max(0, Math.round((valorTotal - valorSinal) * 100) / 100);

  return {
    subtotal: subtotalArredondado,
    valorDesconto,
    valorTotal,
    valorSinal,
    valorRestante
  };
}

/**
 * Formata um valor numérico para Moeda Brasileira (BRL).
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
}

/**
 * Formata uma data ISO para o padrão brasileiro DD/MM/AAAA.
 */
export function formatDateBR(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
}

/**
 * Máquina de estados para transições de status do orçamento.
 */
export const STATUS_TRANSITIONS: Record<OrcamentoStatus, OrcamentoStatus[]> = {
  rascunho: ['enviado', 'recusado'],
  enviado: ['visualizado', 'aprovado', 'sinal_pago', 'recusado'],
  visualizado: ['aprovado', 'sinal_pago', 'recusado'],
  aprovado: ['sinal_pago', 'concluido', 'recusado'],
  sinal_pago: ['concluido', 'recusado'],
  concluido: [],
  recusado: ['rascunho']
};

export function canTransitionStatus(from: OrcamentoStatus, to: OrcamentoStatus): boolean {
  if (from === to) return true;
  return STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

export function getStatusLabel(status: OrcamentoStatus): string {
  const map: Record<OrcamentoStatus, string> = {
    rascunho: 'Rascunho',
    enviado: 'Enviado',
    visualizado: 'Visualizado',
    aprovado: 'Aprovado',
    sinal_pago: 'Sinal Pago (Pix)',
    concluido: 'Concluído',
    recusado: 'Recusado'
  };
  return map[status] || status;
}

export function getStatusBadgeClass(status: OrcamentoStatus): string {
  return `badge badge-${status}`;
}
